import { useEffect, useState } from 'react';
import {
  LEVELER_DEFAULT_STEPS,
  LEVELER_LIMITS,
  type PhoneFacing,
} from '@camper-diary/shared';
import {
  loadLevelerConfig,
  resetLevelerTuning,
  updateLevelerConfig,
  type LevelerConfig,
} from '../api/leveler';
import { useTilt } from '../hooks/useTilt';
import { formatDeg } from '../utils/tilt';

type Note = { kind: 'good' | 'warn' | 'bad'; text: string } | null;

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Errore');

/**
 * Configurazione della livella: misure del camper, gradini dei cunei, punto zero
 * dei sensori e fattori di risposta. Sta in Impostazioni e non accanto alla
 * livella perché è roba da fare una volta sola, con calma, non in piazzola.
 */
export function LevelerSettings() {
  const [config, setConfig] = useState<LevelerConfig | null>(null);
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState(false);

  // misure del camper
  const [track, setTrack] = useState('');
  const [wheelbase, setWheelbase] = useState('');
  const [facing, setFacing] = useState<PhoneFacing>('front');

  // gradini: stringhe, non numeri, perché mentre si digita un campo può essere vuoto
  const [steps, setSteps] = useState<string[]>([]);

  const { reading, status, start, stop } = useTilt();

  function apply(c: LevelerConfig) {
    setConfig(c);
    if (c.vehicle) {
      setTrack(String(c.vehicle.trackCm));
      setWheelbase(String(c.vehicle.wheelbaseCm));
      setFacing(c.vehicle.phoneFacing);
    }
    setSteps((c.wedges?.wedgeA ?? LEVELER_DEFAULT_STEPS).map(String));
  }

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { config: loaded, offline } = await loadLevelerConfig();
        if (!alive) return;
        apply(loaded);
        if (offline) {
          setNote({
            kind: 'warn',
            text: 'Sei senza rete: vedi l\'ultima configurazione salvata. Le modifiche non verranno registrate finché non torna la connessione.',
          });
        }
      } catch (e) {
        if (!alive) return;
        setSteps(LEVELER_DEFAULT_STEPS.map(String));
        setNote({ kind: 'bad', text: `Configurazione non caricata: ${errText(e)}` });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // I sensori restano accesi solo finché serve calibrare.
  useEffect(() => () => stop(), [stop]);

  /* ---- salvataggi ------------------------------------------------------- */

  async function saveVehicle() {
    setBusy(true);
    try {
      apply(
        await updateLevelerConfig({
          vehicle: {
            trackCm: Number(track),
            wheelbaseCm: Number(wheelbase),
            phoneFacing: facing,
          },
        }),
      );
      setNote({ kind: 'good', text: 'Misure del camper salvate.' });
    } catch (e) {
      setNote({ kind: 'bad', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function saveWedges() {
    const values = steps.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    if (values.length === 0) {
      setNote({ kind: 'bad', text: 'Inserisci almeno un gradino con un\'altezza in centimetri.' });
      return;
    }
    setBusy(true);
    try {
      // wedgeB omesso: i due cunei sono considerati identici, è il server a
      // duplicare il valore. Se un giorno saranno diversi si aggiunge qui.
      const saved = await updateLevelerConfig({ wedges: { wedgeA: values } });
      apply(saved);
      setNote({ kind: 'good', text: 'Gradini salvati e messi in ordine di altezza.' });
    } catch (e) {
      setNote({ kind: 'bad', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function saveCalibration() {
    if (!reading) return;
    setBusy(true);
    try {
      apply(
        await updateLevelerConfig({
          calibration: {
            refX: reading.raw.x,
            refY: reading.raw.y,
            refZ: reading.raw.z,
            offsetRoll: reading.roll,
            offsetPitch: reading.pitch,
          },
        }),
      );
      setNote({ kind: 'good', text: 'Punto zero salvato. Da adesso le letture partono da qui.' });
    } catch (e) {
      setNote({ kind: 'bad', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function clearCalibration() {
    if (!confirm('Cancellare la calibrazione? Andrà rifatta su superficie verificata piana.')) {
      return;
    }
    setBusy(true);
    try {
      apply(await updateLevelerConfig({ calibration: null }));
      setNote({ kind: 'warn', text: 'Calibrazione cancellata. Rifalla su superficie verificata piana.' });
    } catch (e) {
      setNote({ kind: 'bad', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  async function resetTuning() {
    if (!confirm('Riportare i fattori a 1.00? Si perdono le misure raccolte finora.')) return;
    setBusy(true);
    try {
      apply(await resetLevelerTuning());
      setNote({ kind: 'warn', text: 'Fattori riportati a 1.00: si riparte dalla pura geometria.' });
    } catch (e) {
      setNote({ kind: 'bad', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  /* ---- render ----------------------------------------------------------- */

  const sensorMessage: Record<string, string> = {
    insecure: 'Questa pagina non è su HTTPS: il browser non concede l\'accesso ai sensori.',
    unsupported: 'Questo dispositivo non espone i sensori di movimento. Serve un telefono.',
    denied:
      'Permesso negato. Su iPhone: Impostazioni → Safari → Movimento e orientamento, poi ricarica la pagina.',
  };

  const tuning = config?.tuning;

  return (
    <section className="leveler-section">
      {/* Nessun titolo di sezione: la scheda attiva qui sopra dice già "Livella". */}
      <p className="muted">
        Misure, cunei e taratura della livella. Si compila una volta sola: in piazzola
        serve solo aprire <strong>Livella</strong>.
      </p>

      {note && <div className={`note ${note.kind}`}>{note.text}</div>}

      {/* ---- 1. Camper ---------------------------------------------------- */}
      <h3>🚐 Misure del camper</h3>
      <p className="muted">
        Servono per tradurre l'inclinazione in centimetri di gradino. Misura da centro
        gomma a centro gomma.
      </p>

      <div className="leveler-grid">
        <label className="leveler-field">
          <span>Carreggiata anteriore (cm)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            min={LEVELER_LIMITS.track.min}
            max={LEVELER_LIMITS.track.max}
            placeholder="172"
            value={track}
            onChange={(e) => setTrack(e.target.value)}
          />
        </label>
        <label className="leveler-field">
          <span>Passo (cm)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            min={LEVELER_LIMITS.wheelbase.min}
            max={LEVELER_LIMITS.wheelbase.max}
            placeholder="370"
            value={wheelbase}
            onChange={(e) => setWheelbase(e.target.value)}
          />
        </label>
      </div>

      <label className="leveler-field">
        <span>Come appoggi il telefono</span>
        <select value={facing} onChange={(e) => setFacing(e.target.value as PhoneFacing)}>
          <option value="front">Parte alta dello schermo verso il muso</option>
          <option value="rear">Parte alta dello schermo verso la coda</option>
        </select>
      </label>
      <p className="muted hint">
        Decide il verso del beccheggio. Sbagliarlo fa consigliare l'esatto contrario.
      </p>

      <div className="btn-row">
        <button className="btn btn-primary" onClick={() => void saveVehicle()} disabled={busy}>
          Salva le misure
        </button>
      </div>

      {/* ---- 2. Cunei ----------------------------------------------------- */}
      <h3>🪜 Gradini dei cunei</h3>
      <p className="muted">
        Misura l'altezza di ogni gradino con il cuneo appoggiato a terra, dal suolo al
        piano dove va la gomma. I due cunei sono considerati identici.
      </p>

      <div className="steps">
        {steps.map((value, i) => (
          <div className="step-line" key={i}>
            <span className="step-no">{i + 1}</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min={LEVELER_LIMITS.step.min}
              aria-label={`Altezza del gradino ${i + 1}`}
              value={value}
              onChange={(e) =>
                setSteps((s) => s.map((v, j) => (j === i ? e.target.value : v)))
              }
            />
            <button
              className="btn btn-ghost"
              onClick={() => setSteps((s) => s.filter((_, j) => j !== i))}
            >
              Togli
            </button>
          </div>
        ))}
      </div>

      <div className="btn-row two">
        <button
          className="btn btn-ghost"
          onClick={() => setSteps((s) => [...s, ''])}
          disabled={busy || steps.length >= LEVELER_LIMITS.maxSteps}
        >
          Aggiungi gradino
        </button>
        <button className="btn btn-primary" onClick={() => void saveWedges()} disabled={busy}>
          Salva i gradini
        </button>
      </div>

      {/* ---- 3. Calibrazione ---------------------------------------------- */}
      <h3>🎯 Punto zero</h3>
      <p className="muted">
        Da fare <strong>una volta sola</strong>, con il camper su una superficie che hai
        verificato piana con una livella vera. Appoggia il telefono nel punto che userai
        sempre, e non toccarlo.
      </p>

      {sensorMessage[status] && <div className="note bad">{sensorMessage[status]}</div>}

      <div className="readout">
        <div className="cell">
          <span className="tag">Rollio grezzo</span>
          <span className="value">{reading ? formatDeg(reading.roll) : '—'}</span>
        </div>
        <div className="cell">
          <span className="tag">Beccheggio grezzo</span>
          <span className="value">{reading ? formatDeg(reading.pitch) : '—'}</span>
        </div>
      </div>

      <p className="steady" data-steady={String(Boolean(reading?.steady))}>
        {reading?.steady ? 'Telefono fermo — puoi calibrare' : 'Telefono in movimento'}
      </p>

      <div className="btn-row">
        <button
          className="btn btn-ghost"
          onClick={() => void start()}
          disabled={status === 'running'}
        >
          {status === 'running' ? 'Sensori attivi' : 'Attiva i sensori'}
        </button>
        <button
          className="btn btn-primary"
          onClick={() => void saveCalibration()}
          disabled={busy || !reading?.steady}
        >
          Salva questo come punto zero
        </button>
        <button
          className="btn btn-danger"
          onClick={() => void clearCalibration()}
          disabled={busy || !config?.calibration}
        >
          Cancella la calibrazione
        </button>
      </div>

      <p className="muted">
        {config?.calibration
          ? `Calibrato il ${new Date(config.calibration.calibratedAt).toLocaleString('it-IT')}.`
          : 'Nessuna calibrazione salvata. Senza punto zero le letture non sono attendibili.'}
      </p>

      {/* ---- 4. Taratura --------------------------------------------------- */}
      <h3>⚖️ Fattori di risposta</h3>
      <p className="muted">
        Quanto il camper si raddrizza davvero rispetto al calcolo geometrico. Li impara la
        modalità verifica, misura dopo misura. <strong>1.00</strong> significa pura geometria.
      </p>

      <div className="readout">
        <div className="cell">
          <span className="tag">Rollio</span>
          <span className="value">{tuning ? tuning.gainRoll.toFixed(2) : '—'}</span>
        </div>
        <div className="cell">
          <span className="tag">Beccheggio</span>
          <span className="value">{tuning ? tuning.gainPitch.toFixed(2) : '—'}</span>
        </div>
      </div>

      <p className="muted">
        {!tuning || tuning.samples === 0
          ? 'Nessuna misura raccolta: i fattori sono quelli di partenza.'
          : `Basato su ${tuning.samples} ${tuning.samples === 1 ? 'misura' : 'misure'} in piazzola.`}
      </p>

      <div className="btn-row">
        <button
          className="btn btn-danger"
          onClick={() => void resetTuning()}
          disabled={busy || !tuning || tuning.samples === 0}
        >
          Riporta tutto a 1.00
        </button>
      </div>
    </section>
  );
}
